/**
 * Scroll a highlighted session card into view inside an embed widget.
 *
 * Widgets render in an open Shadow DOM, so `document.querySelector` never
 * finds `[data-session-id]`. Query the inner scroller instead.
 *
 * SB-8207: `behavior: 'smooth'` is a no-op in open Shadow DOM, and the
 * attached list ref is sometimes not the overflowing box on the first
 * layout pass. Walk to the nearest overflow ancestor and jump instantly,
 * then retry after layout settles.
 */

const escapeAttr = id =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(id)
    : String(id).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

/**
 * @param {ParentNode | null} root
 * @param {string} sessionId
 * @returns {Element | null}
 */
export function findSessionCard(root, sessionId) {
  if (!root || !sessionId || typeof root.querySelector !== 'function') return null;
  return root.querySelector(`[data-session-id="${escapeAttr(sessionId)}"]`);
}

/**
 * @param {Element | null} start
 * @param {HTMLElement | null} [fallback]
 * @returns {HTMLElement | null}
 */
function isOverflowingBox(node) {
  return (
    !!node &&
    typeof node.scrollHeight === 'number' &&
    typeof node.scrollTop === 'number' &&
    node.scrollHeight > (node.clientHeight || 0) + 1
  );
}

export function nearestOverflowScroller(start, fallback) {
  // The widget list ref is the intended scroller. A taller ancestor with
  // overflow:visible can still report scrollHeight > clientHeight and steal
  // the jump (SB-8207).
  if (isOverflowingBox(fallback)) return fallback;

  let node = start;
  while (node) {
    if (isOverflowingBox(node)) {
      return node;
    }
    node = node.parentElement || node.parentNode;
    if (node && node.nodeType && node.nodeType !== 1) {
      node = node.parentElement;
    }
  }
  return fallback || null;
}

function jumpScrollerToCard(scroller, el) {
  if (!scroller || !el) return;
  if (typeof scroller.getBoundingClientRect !== 'function' || typeof el.getBoundingClientRect !== 'function') {
    return;
  }
  const containerRect = scroller.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const nextTop =
    scroller.scrollTop +
    (elRect.top - containerRect.top) -
    scroller.clientHeight / 2 +
    elRect.height / 2;
  scroller.scrollTop = Math.max(0, nextTop);
}

/**
 * @param {HTMLElement | null} container
 * @param {string} sessionId
 * @returns {boolean}
 */
export function scrollSessionCardIntoView(container, sessionId) {
  let el = findSessionCard(container, sessionId);
  if (!el && container && typeof container.getRootNode === 'function') {
    const root = container.getRootNode();
    el = findSessionCard(root, sessionId);
  }
  if (!el) return false;

  const scroller = nearestOverflowScroller(el, container);
  if (scroller) {
    jumpScrollerToCard(scroller, el);
    return true;
  }

  if (typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'auto', block: 'center' });
  }
  return true;
}

/**
 * Retry after layout / day-page switches. Even a successful first jump is
 * repeated twice because Shadow DOM cards can still be measuring.
 *
 * @param {(() => HTMLElement | null) | HTMLElement | null} getContainer
 * @param {string} sessionId
 * @param {{ attempts?: number }} [opts]
 */
export function scheduleScrollSessionIntoView(getContainer, sessionId, { attempts = 8 } = {}) {
  let n = 0;
  const delays = [0, 0, 50, 100, 200, 400, 500, 500];
  const tick = () => {
    const container =
      typeof getContainer === 'function' ? getContainer() : getContainer;
    const ok = scrollSessionCardIntoView(container, sessionId);
    n += 1;
    if (n >= attempts) return;
    if (!ok || n < 3) {
      const wait = delays[Math.min(n, delays.length - 1)];
      if (wait === 0 && typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(tick);
      } else {
        setTimeout(tick, wait);
      }
    }
  };
  tick();
}

/**
 * Walk highlighted ids and pick the first that exists on a rendered day.
 * @param {Iterable<string>} ids
 * @param {(id: string) => number} findDayIndex  returns >= 0 when present
 * @returns {{ id: string, dayIndex: number } | null}
 */
export function firstDatedHighlight(ids, findDayIndex) {
  const list = [...ids];
  for (const id of list) {
    const dayIndex = findDayIndex(id);
    if (dayIndex >= 0) return { id, dayIndex };
  }
  return list.length ? { id: list[0], dayIndex: -1 } : null;
}

/**
 * @param {object | null | undefined} agendaData
 * @param {string} sessionId
 */
export function findAgendaDayIndex(agendaData, sessionId) {
  const keys = agendaData?.orderedDateKeys || [];
  return keys.findIndex(key => {
    const day = agendaData?.tablesByDay?.[key];
    if (!day?.sessionsByLocation) return false;
    return Object.values(day.sessionsByLocation).some(sessions =>
      (sessions || []).some(s => s.id === sessionId)
    );
  });
}

/**
 * @param {object | null | undefined} scheduleData
 * @param {string} sessionId
 */
export function findScheduleDayIndex(scheduleData, sessionId) {
  const tablesByDay = scheduleData?.data?.tablesByDay;
  const keys = scheduleData?.data?.orderedDateKeys || [];
  return keys.findIndex(key => {
    const slots = tablesByDay?.[key]?.table?.sessionsByTime || [];
    return slots.some(slot =>
      (slot.timeItems || []).some(item => (item.session || item).id === sessionId)
    );
  });
}
